import Analytics from 'analytics'
import Environment from 'entities/Environment'

import { getTenant, getTenantLanguage } from 'utils/getTenantSpecificValues'
import { sentryLog } from 'utils/sentryLogging'
import { getApiBaseUrl } from 'utils/url'

const normalizeUrl = (url: string) => encodeURI(decodeURIComponent(url))

const normalizedProperties = (data: any) => {
  if (data?.properties?.url) {
    // eslint-disable-next-line no-param-reassign
    data.properties.url = normalizeUrl(data.properties.url)
  } else {
    // eslint-disable-next-line no-param-reassign
    data.properties.url = normalizeUrl(window.location.href)
  }

  const referrer = data?.properties?.referrer || window.document.referrer
  if (referrer) {
    // eslint-disable-next-line no-param-reassign
    data.properties.referrer = normalizeUrl(referrer)
  }
}

const sanitizePayloadForNow = (payload: any) => {
  if (/^\//.test(payload.properties.url)) {
    /* eslint-disable no-param-reassign */
    payload.properties.path = payload.properties.url
    payload.properties.url = window.location.href
    /* eslint-enable no-param-reassign */
  }
}

const sendAnalyticRequest = async (data: any) => {
  const customPayload = data.properties?.customPayload || {}
  /* eslint-disable no-param-reassign */
  data.properties.customPayload = {
    ...customPayload,
    appVersion: process.env.REACT_APP_VERSION,
  }
  data.properties.tenant = getTenant()
  data.properties.language = getTenantLanguage()
  /* eslint-enable no-param-reassign */

  if (process.env.NODE_ENV !== Environment.production) {
    // eslint-disable-next-line no-console
    console.log(`STAGING: ${data.type} action was fired:`, data)
    return
  }

  const url = `${getApiBaseUrl()}/analytics`

  try {
    await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
      },
      body: JSON.stringify(data),
    })
    /**
     * we can ignore it here
     * the analytics endpoints is forwarded to cloud function
     * errors can be found within gcp logs
     */
  } catch (error) {
    sentryLog(error as Error)
  }
}

const ourPlugin = () => ({
  name: 'sae-analytics',
  page: async ({ payload }: { payload: any }) => {
    sanitizePayloadForNow(payload)
    normalizedProperties(payload)
    await sendAnalyticRequest({ ...payload, type: 'page' })
  },
  track: async ({ payload, instance }: { payload: any; instance: any }) => {
    const {
      page: {
        last: { properties },
      },
    } = instance.getState()
    sanitizePayloadForNow(payload)
    const data = {
      ...payload,
      properties: { ...properties, ...payload.properties },
    }
    normalizedProperties(data)
    await sendAnalyticRequest(data)
  },
})

const config = {
  name: 'app-analytics',
  plugins: [ourPlugin()],
}

export default Analytics(config) as any
